/**
 * Generic color-name -> hex fallback for SanMar swatch rendering (frontend).
 *
 * Mirror of the backend map
 * `api/src/integration_resources/sanmar/color_hex_fallback.py`. Keep the two
 * in sync.
 *
 * Why a frontend copy: the backend fills `hex` from this map when a color
 * has no `vendor_color_hex` row ("Mode B"), but the editor's color filmstrip
 * can ALSO synthesize a swatch for a color the live SanMar catalog never
 * returned ("Mode A" -- the CP's `selected_color_sku` is not one of the
 * style's catalog colors). Those synthesized rows never pass through the
 * backend endpoint, so the frontend applies the same fallback locally to
 * avoid a grey swatch.
 *
 * A swatch whose hex comes from this fallback (either flagged by the backend
 * `is_hex_fallback` OR synthesized here) is marked as approximate in the UI.
 */

// Resolved-English-name -> hex. Mirrors GENERIC_COLOR_HEX_FALLBACK.
export const GENERIC_COLOR_HEX_FALLBACK = {
    White: "#FFFFFF",
    Black: "#000000",
    "Deep Black": "#0A0A0A",
    "Jet Black": "#0B0B0B",
    Navy: "#1F2A44",
    "True Navy": "#1A2B5F",
    "Strong Navy": "#1B2A48",
    Royal: "#05397B",
    "True Royal": "#1E3F8C",
    "Athletic Heather": "#9BA1A7",
    "Heather Grey": "#9CA3AF",
    "Dark Heather Grey": "#434C4B",
    "Grey Steel": "#7C8087",
    "Steel Grey": "#6E7479",
    Charcoal: "#3A3B3C",
    Red: "#C8102E",
    "Athletic Red": "#B22234",
    Cardinal: "#721B24",
    Maroon: "#5A1D2C",
    Orange: "#E2581E",
    "Texas Orange": "#BF5700",
    "Safety Orange": "#FF6A13",
    Gold: "#F6A21C",
    "Athletic Gold": "#E59A0B",
    Yellow: "#FEDB00",
    "Safety Green": "#C2E812",
    "Kelly Green": "#0F8469",
    "Forest Green": "#21462E",
    Hunter: "#2C5234",
    "Carolina Blue": "#6395CA",
    "Sky Blue": "#8AB6D6",
    "Light Blue": "#ADD8E6",
    Purple: "#33295E",
    Pink: "#E5779A",
    Brown: "#5A4534",
    Chocolate: "#40362D",
    Sand: "#D9C7A3",
    Khaki: "#B7A98B",
    Stone: "#CABFA3",
    Blue: "#1E3A8C",
    Green: "#1F8A4C",
    Grey: "#8A8F94",
    Gray: "#8A8F94",
};

const LOWER_INDEX = Object.fromEntries(
    Object.entries(GENERIC_COLOR_HEX_FALLBACK).map(([name, hex]) => [
        name.toLowerCase(),
        hex,
    ]),
);

/**
 * Return a fallback hex for a color name, or null if unmapped.
 * Exact match first (canonical casing), then case-insensitive.
 * @param {string|null|undefined} colorName
 * @returns {string|null}
 */
export function fallbackHex(colorName) {
    if (!colorName) return null;
    const direct = GENERIC_COLOR_HEX_FALLBACK[colorName];
    if (direct != null) return direct;
    return LOWER_INDEX[String(colorName).trim().toLowerCase()] ?? null;
}
